import { Fragment, useState } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { type ActionItem, type IColumn, Tooltip, Typography } from 'shared/ui'
import { SwitchAction } from 'shared/ui/SwitchAction'
import { DayjsFormats, logger } from 'shared/lib'
import dayjs from 'shared/lib/dayjs'
import { uiStore } from 'shared/store/uiStore'
import { type IWorkflow } from 'entities/Workflow'
import { useWorkflowListContext } from 'pages/workflows/context/workflowListContext'
import { WorkflowStatusBadge } from 'pages/workflows/ui'
import { workflowOperation } from 'pages/workflows/store/operationStore'
import styles from './styles.module.scss'

export type IWorkflowRow = {
  id: string
  workflow: IWorkflow
}

const toggleWorkflowActive = async (workflow: IWorkflow): Promise<void> => {
  try {
    const payload = workflow.enabled
      ? await workflowOperation.disable(workflow)
      : await workflowOperation.enable(workflow)
    if (payload) workflow.syncOrigin(payload)
  } catch (error) {
    logger.error(error)
  }
}

const NameCell = observer(({ workflow }: { workflow: IWorkflow }) => (
  <div className={styles.nameCellWrapper}>
    <Typography
      variant={'body-md-regular'}
      ellipsis
      tooltipProps={{
        label: workflow.name,
      }}
      ariaLabel={'name'}
      className={styles.nameText}
    >
      {workflow.name}
    </Typography>
    <Tooltip label={workflow.isInvalid && 'Suspicious content detected'} placement={'top'}>
      <div className={styles.nameToggle}>
        <SwitchAction
          size={'small'}
          active={workflow.enabled}
          disabled={workflow.isInvalid}
          onTrigger={() => toggleWorkflowActive(workflow)}
          ariaLabel={`WorkflowActiveToggle-${workflow.id}`}
        />
      </div>
    </Tooltip>
  </div>
))

const COLUMNS: IColumn<IWorkflowRow>[] = [
  {
    field: 'name',
    name: 'Name',
    width: '100%',
    maxWidth: '300px',
    minWidth: '300px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => <NameCell workflow={workflow} />,
  },
  {
    field: 'status',
    name: 'Status',
    minWidth: '84px',
    maxWidth: '120px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => (
      <Tooltip label={workflow.isInvalid && 'Suspicious content detected'} placement={'top'}>
        <WorkflowStatusBadge isInvalid={workflow.isInvalid} active={workflow.enabled} />
      </Tooltip>
    ),
  },
  {
    field: 'enrolled',
    name: 'Enrolled',
    minWidth: '84px',
    maxWidth: '120px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ workflow }) => workflow.executionsCount,
  },
  {
    field: 'created_at',
    name: 'Created',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ workflow }) => dayjs(workflow.createdAt).format(DayjsFormats.fullWithAtDash2),
  },
  {
    field: 'last_modified',
    name: 'Last modified',
    minWidth: '120px',
    maxWidth: '260px',
    renderRowCell: ({ workflow }) =>
      !!workflow.updatedAt ? (
        dayjs(workflow.updatedAt).format(DayjsFormats.fullWithAtDash2)
      ) : (
        <Fragment />
      ),
  },
]

export const useWorkflowTable = () => {
  const [columnsIds, setColumnIds] = useState(() => COLUMNS.map(({ field }) => field))
  const { items, loadData } = useWorkflowListContext()

  const duplications = useLocalObservable(() => new Set<string>())
  const deletions = useLocalObservable(() => new Set<string>())

  const makeEditAction = (workflow: IWorkflow): ActionItem => ({
    icon: 'edit',
    text: 'Edit',
    onAction: () => {
      uiStore.changeRoute({ path: `/workflows/view/${workflow.id}` })
    },
  })

  const makeDuplicateAction = (workflow: IWorkflow): ActionItem => ({
    icon: 'copy',
    text: 'Duplicate',
    buttonProps: {
      disabled: duplications.has(workflow.id) || deletions.has(workflow.id),
      loading: duplications.has(workflow.id),
    },
    onAction: () => {
      duplications.add(workflow.id)
      workflowOperation.duplicate(workflow).then((duplicated) => {
        duplications.delete(workflow.id)

        if (duplicated) uiStore.changeRoute({ path: `/workflows/view/${duplicated.id}` })
      })
    },
  })

  const makeDeleteAction = (workflow: IWorkflow): ActionItem => ({
    icon: 'delete',
    text: 'Delete',
    buttonProps: {
      disabled: deletions.has(workflow.id) || duplications.has(workflow.id),
      loading: deletions.has(workflow.id),
    },
    onAction: () => {
      deletions.add(workflow.id)
      workflowOperation.delete(workflow).then((deleted) => {
        deletions.delete(workflow.id)

        if (deleted) loadData()
      })
    },
  })

  const columns = COLUMNS
  const rows = items.map((workflow) => {
    const actions: ActionItem[] = [
      makeEditAction(workflow),
      makeDuplicateAction(workflow),
      makeDeleteAction(workflow),
    ]

    return { id: workflow.id, workflow, actionsProps: { actions } }
  })

  return { rows, columns, columnsIds, setColumnIds }
}
